import React, { useEffect, useState } from 'react';
import Icon from 'components/AppIcon';

const MODE_STEPS = {
  'text-to-game': [
    'Analyzing prompt...',
    'Designing game mechanics...',
    'Generating art assets...',
    'Building game logic...',
    'Compiling & testing...',
    'Finalizing game...',
  ],
  'prompt-to-mini-game': [
    'Analyzing prompt...',
    'Designing mini game rules...',
    'Generating game assets...',
    'Building game logic...',
    'Testing gameplay...',
    'Finalizing mini game...',
  ],
  'text-to-animation': [
    'Analyzing scene description...',
    'Designing animation frames...',
    'Generating character assets...',
    'Building animation timeline...',
    'Rendering animation...',
    'Finalizing scene...',
  ],
  'script-to-video': [
    'Parsing script...',
    'Designing scene layout...',
    'Generating visual assets...',
    'Compositing scenes...',
    'Rendering video...',
    'Finalizing video...',
  ],
  'music-to-video': [
    'Validating source...',
    'Importing audio...',
    'Analyzing duration / BPM / waveform...',
    'Gathering visuals...',
    'Generating scenes...',
    'Rendering video...',
    'Merging audio + captions...',
    'Finalizing export...',
    'Saving to library...',
  ],
};

const MODE_LABELS = {
  'text-to-game': 'Generating your game',
  'prompt-to-mini-game': 'Generating your mini game',
  'text-to-animation': 'Generating your animation',
  'script-to-video': 'Generating your video',
  'music-to-video': 'Generating your music video',
};

export default function GenerationProgress({ isGenerating, progress, mode, onCancel }) {
  const [stepIndex, setStepIndex] = useState(0);
  const steps = MODE_STEPS?.[mode] || MODE_STEPS?.['text-to-game'];
  const label = MODE_LABELS?.[mode] || 'Generating your content';

  useEffect(() => {
    if (!isGenerating) { setStepIndex(0); return; }
    const interval = setInterval(() => {
      setStepIndex((prev) => (prev < steps?.length - 1 ? prev + 1 : prev));
    }, 2200);
    return () => clearInterval(interval);
  }, [isGenerating, steps?.length]);

  if (!isGenerating) return null;

  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-4 animate-fade-in-up"
      style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))', border: '1px solid var(--color-border)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center pulse-glow" style={{ background: 'var(--color-primary)' }}>
            <Icon name="Cpu" size={16} color="white" />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              LoopNest AI is generating your content...
            </p>
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              {steps?.[stepIndex]}
            </p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-xs px-3 py-1.5 rounded-lg border transition-all hover:opacity-80"
          style={{ borderColor: 'var(--color-error)', color: 'var(--color-error)', background: 'rgba(239,68,68,0.08)' }}
        >
          Cancel
        </button>
      </div>
      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs mb-1.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>
          <span>{label}...</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))' }}
          />
        </div>
      </div>
      {/* Steps */}
      <div className="flex gap-1.5 flex-wrap">
        {steps?.map((step, i) => (
          <div
            key={i}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
            style={{
              background: i < stepIndex ? 'rgba(16,185,129,0.1)' : i === stepIndex ? 'rgba(99,102,241,0.12)' : 'var(--color-muted)',
              color: i < stepIndex ? 'var(--color-success)' : i === stepIndex ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
              fontFamily: 'var(--font-caption)',
            }}
          >
            <Icon
              name={i < stepIndex ? 'CheckCircle2' : i === stepIndex ? 'Loader' : 'Circle'}
              size={10}
              color={i < stepIndex ? 'var(--color-success)' : i === stepIndex ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
            />
            <span className="hidden sm:inline">{step?.replace('...', '')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}